import { useCallback, useState } from 'react';
import { useUserContext } from '../../user/contexts/user.context';
import { useLocation, useParams } from 'react-router-dom';
import { RouteBuilder, RouteNames } from '../../router/constantes/routes.const';
import { ParamsNames } from '../../router/constantes/paramsNames';
import { useLocale } from '../../router/hooks/use-locale.hook';

// Locales valides supportées par l'application
const VALID_LOCALES = ["en-US", "fr-FR"];

export const useHeader = () => {
  const location = useLocation();
  const params = useParams<{ [key: string]: string }>();
  const [isPopoverOpened, togglePopover] = useState(false);

  const { getUser, getCurrentOrganization, currentOrganization } = useUserContext();
  const organizationCodeFromUrl = params[ParamsNames.ORGANIZATION_CODE];
  const locale = useLocale();

  const goToHome = useCallback((organizationCodeParam?: string) => {
    let orgCode = organizationCodeParam || organizationCodeFromUrl;
    if (!orgCode) {
      try {
        orgCode = getCurrentOrganization().code;
      } catch (error) {
        // Si l'organisation n'est pas encore chargée, ne pas rediriger
        return;
      }
    }
    window.location.href = RouteBuilder.home(locale, orgCode);
  }, [organizationCodeFromUrl, getCurrentOrganization, locale]);

  const onOrganizationSelectValueChanged = useCallback(
    (code: string) => {
      console.log('[use-header] onOrganizationSelectValueChanged appelé avec code:', code);
      console.log('[use-header] location.pathname:', location.pathname);
      
      // Fermer le popover d'abord
      togglePopover(false);
      
      if(location.pathname === RouteNames.errorPage404 || location.pathname === RouteNames.errorPage500) {
        console.log('[use-header] Redirection vers la page d\'accueil (erreur)');
        goToHome(code);
        return;
      }
      
      // Extraire le chemin après /organization/code pour le conserver
      const currentPath = location.pathname;
      
      // Vérifier que currentPath existe avant de l'utiliser
      if (!currentPath) {
        console.log('[use-header] Pas de currentPath, redirection vers la page d\'accueil');
        const newPath = RouteBuilder.home(locale, code);
        console.log('[use-header] newPath:', newPath);
        window.location.href = newPath;
        return;
      }
      
      // Extraire le chemin après /organization/code
      let pathWithoutLocaleAndOrg = "/";
      const orgIndex = currentPath.indexOf("/organization/");
      
      if (orgIndex !== -1) {
        // Format avec /organization/code
        const afterOrg = currentPath.substring(orgIndex + "/organization/".length);
        const orgCodeEnd = afterOrg.indexOf("/");
        if (orgCodeEnd !== -1) {
          // Il y a un chemin après le code organisation
          pathWithoutLocaleAndOrg = afterOrg.substring(orgCodeEnd);
        } else {
          // Pas de chemin après le code organisation, on est sur la page d'accueil
          pathWithoutLocaleAndOrg = "/";
        }
      } else {
        // Vérifier si on est sur la page d'accueil sans organisation dans l'URL (ancien format)
        const pathParts = currentPath.split('/').filter(p => p);
        if (pathParts.length === 0 || (pathParts.length === 1 && VALID_LOCALES.includes(pathParts[0]))) {
          // On est sur la page d'accueil
          pathWithoutLocaleAndOrg = "/";
        } else if (pathParts.length > 1 && VALID_LOCALES.includes(pathParts[0])) {
          // Ancien format avec locale mais sans /organization/
          pathParts.shift(); // Enlever le locale
          if (pathParts.length > 0) {
            // Il pourrait y avoir un ancien code organisation ou un chemin
            // Si le premier élément ressemble à un code organisation (pas de slash), on l'enlève
            const firstPart = pathParts[0];
            if (firstPart && !firstPart.includes("/")) {
              pathParts.shift(); // Enlever l'ancien code organisation s'il existe
            }
          }
          pathWithoutLocaleAndOrg = "/" + pathParts.join("/");
        } else {
          // Format inconnu, extraire ce qui vient après le locale
          pathWithoutLocaleAndOrg = "/" + pathParts.slice(1).join("/");
        }
      }
      
      // Normaliser le chemin (enlever le trailing slash sauf pour la racine)
      if (pathWithoutLocaleAndOrg && pathWithoutLocaleAndOrg !== "/" && pathWithoutLocaleAndOrg.endsWith("/")) {
        pathWithoutLocaleAndOrg = pathWithoutLocaleAndOrg.slice(0, -1);
      }
      
      if (!pathWithoutLocaleAndOrg || pathWithoutLocaleAndOrg === "") {
        pathWithoutLocaleAndOrg = "/";
      }
      
      console.log('[use-header] pathWithoutLocaleAndOrg:', pathWithoutLocaleAndOrg);
      
      // Construire la nouvelle URL avec le nouveau code organisation
      // Si on est sur la page d'accueil (pathWithoutLocaleAndOrg === "/"), utiliser RouteBuilder.home
      const newPath = pathWithoutLocaleAndOrg === "/" 
        ? RouteBuilder.home(locale, code)
        : `/${locale}/organization/${code}${pathWithoutLocaleAndOrg}`;
      
      console.log('[use-header] newPath final:', newPath);
      console.log('[use-header] Appel de window.location.href');
      
      // Faire un refresh navigateur pour recharger complètement l'application avec la nouvelle organisation
      // Cela garantit que tous les composants et hooks sont réinitialisés avec la nouvelle organisation
      window.location.href = newPath;
    },
    [togglePopover, location, goToHome, locale]
  );

  return {
    isPopoverOpened,
    togglePopover,
    onOrganizationSelectValueChanged,
    getUser,
    getCurrentOrganization,
    currentOrganization,
  };
};
